import { findTreatedIssue } from "src/features/integrations/issues/issueTreatments.ts";
import type { FusionHubDataSourceWithIntegration } from "src/features/integrations/overview/table/IntegrationsTableContent.tsx";

export type SyncStatusKind =
	| "paused"
	| "attention"
	| "queued"
	| "syncing"
	| "processing"
	| "succeeded";

export const SYNC_STATUS_KINDS: SyncStatusKind[] = [
	"attention",
	"syncing",
	"queued",
	"processing",
	"succeeded",
	"paused",
];

export interface SyncStatusInput {
	currentStatus: FusionHubDataSourceWithIntegration["currentStatus"];
	lastSuccessfulSyncAt: string | null;
	issues: FusionHubDataSourceWithIntegration["issues"];
	isPaused: boolean;
	loadStatus: FusionHubDataSourceWithIntegration["fusionHubIntegration"]["loadStatus"];
	rawMartJobInProgress: boolean;
	currentJobQueuedAt?: string | null | undefined;
	currentJobStartedAt?: string | null | undefined;
}

export const toSyncStatusInput = (
	dataSource: FusionHubDataSourceWithIntegration,
): SyncStatusInput => ({
	currentStatus: dataSource.currentStatus,
	lastSuccessfulSyncAt: dataSource.lastSuccessfulSyncAt,
	issues: dataSource.issues,
	isPaused: dataSource.fusionHubIntegration.isPaused,
	loadStatus: dataSource.fusionHubIntegration.loadStatus,
	rawMartJobInProgress: dataSource.rawMartJobInProgress,
	currentJobQueuedAt: dataSource.currentJobQueuedAt,
	currentJobStartedAt: dataSource.currentJobStartedAt,
});

export const resolveSyncStatusKind = (
	input: SyncStatusInput,
): SyncStatusKind | null => {
	// WHY paused first: same precedence as the fleet table's rowCategory.
	if (input.currentStatus === "paused" || input.isPaused) {
		return "paused";
	}
	if (
		findTreatedIssue(input.issues) ||
		input.currentStatus === "failed" ||
		input.loadStatus === "FAILED"
	) {
		return "attention";
	}
	if (input.currentStatus === "waiting") {
		return "queued";
	}
	// WHY currentStatus before loadStatus (FusionHub#3666): a failing initial load can leave loadStatus stuck at LOADING forever.
	if (input.currentStatus === "running" || input.loadStatus !== "READY") {
		return "syncing";
	}
	if (!input.lastSuccessfulSyncAt) {
		// WHY: integration_status can lag the job table.
		return input.currentStatus === "completed" ? "succeeded" : null;
	}
	// WHY not nexusStatus INITIAL: it can stay INITIAL for weeks after syncs succeed, so only the DLM job flag counts.
	return input.rawMartJobInProgress ? "processing" : "succeeded";
};

export const resolveDataSourceStatusKind = (
	dataSource: FusionHubDataSourceWithIntegration,
): SyncStatusKind | null =>
	resolveSyncStatusKind(toSyncStatusInput(dataSource));
